import { ILineAreaSeries } from '../../../../shared/components/charts/line-area-chart/line-area-chart.component';
import { CylinderChartData } from '../../../../shared/components/charts/bar-charts/cylinder-chart/cylinder-chart.component';
import { IFunnelDataPoint } from '../../../../shared/components/charts/funnel-chart/funnel-chart.component';
import { IsometricBarData } from '../../../../shared/components/charts/isometric-bar/isometric-bar.component';
import { BarChartData } from '../../../../shared/components/charts/bar-charts/multiple-bar/multiple-bar.component';
import { StackedBarData } from '../../../../shared/components/charts/stacked-bar/stacked-bar.component';

export interface DashboardCard {
  title: string;
  value: string;
  icon: string;
  bgColorClass: string;
  loading?: boolean;
  trend?: {
    value: string;
    direction: 'up' | 'down' | 'none';
    label?: string;
    textColorClass?: string;
  };
}

export const ACQUISITION_CARDS: DashboardCard[] = [
  {
    title: 'Total Signups',
    value: '0',
    icon: 'user-plus',
    bgColorClass: 'bg-[#10b981]',
  },
  {
    title: 'Active Ads',
    value: '0',
    icon: 'megaphone',
    bgColorClass: 'bg-[#06b6d4]',
  }
];

export const CONVERSION_CARDS: DashboardCard[] = [
  {
    title: 'Free → Paid',
    value: '0',
    icon: 'circleDollarSign',
    bgColorClass: 'bg-[#ec4899]',
  },
  {
    title: 'Trial → Paid',
    value: '0',
    icon: 'circleDollarSign',
    bgColorClass: 'bg-[#8b5cf6]',
  },
  {
    title: 'Trial to Freemium',
    value: '0',
    icon: 'clock',
    bgColorClass: 'bg-[#f97316]'
  }
];

export const SUBSCRIPTION_CARDS: DashboardCard[] = [
  {
    title: 'Total Active Paid Subscription',
    value: '0',
    icon: 'dollar-sign',
    bgColorClass: 'bg-[#ef4444]',
  },
  {
    title: 'New Paid Subs',
    value: '0',
    icon: 'dollar-sign',
    bgColorClass: 'bg-[#4f46e5]',
  },
  {
    title: 'Net Growth',
    value: '0',
    icon: 'trending-up',
    bgColorClass: 'bg-[#9333ea]',
  },
  {
    title: 'Cancellations',
    value: '0',
    icon: 'circle-x',
    bgColorClass: 'bg-[#f59e0b]'
  },
  {
    title: 'Churn %',
    value: '0',
    icon: 'badge-percent',
    bgColorClass: 'bg-[#3b82f6]'
  }
];

export const MONTHLY_SIGNUP_TREND_CONFIG: ILineAreaSeries[] = [
  {
    key: 'totalSignups',
    name: 'Total Signups',
    lineColor: '#1d4ed8',
    areaColor: 'transparent',
    areaOpacity: 0,
    strokeWidth: 0,
    showPoints: true,
    showDropLines: true,
    dropLineColor: '#bfdbfe',
    pointRadius: 5,
    renderAsBar: false,
    data: []
  },
  {
    key: 'socialSignups',
    name: 'Social Signups',
    lineColor: '#f59e0b',
    areaColor: '#fef3c7',
    areaOpacity: 0.4,
    strokeWidth: 2,
    showPoints: true,
    pointRadius: 5,
    showDropLines: false,
    data: []
  }
];

export const CONVERSION_TREND_CONFIG: ILineAreaSeries[] = [
  {
    key: 'trialStarted',
    name: 'Trial Started',
    lineColor: '#c084fc',
    areaColor: '#c084fc',
    areaOpacity: 0.05,
    strokeWidth: 2,
    lineStyle: 'dashed',
    showPoints: true,
    renderAsBar: false,
    data: []
  },
  {
    key: 'newPaidSubscribers',
    name: 'New Paid Subscribers',
    lineColor: '#f59e0b',
    areaColor: '#fef3c7',
    areaOpacity: 0.4,
    strokeWidth: 2,
    lineStyle: 'solid',
    showPoints: true,
    renderAsBar: false,
    data: []
  },
  {
    key: 'freemiumUsers',
    name: 'Freemium Users',
    lineColor: '#10b981',
    areaColor: '#10b981',
    areaOpacity: 0.05,
    strokeWidth: 1.5,
    lineStyle: 'solid',
    showPoints: true,
    pointRadius: 3,
    renderAsBar: false,
    data: []
  }
];

export const RENEWAL_TREND_CONFIG: ILineAreaSeries[] = [
  {
    key: 'autoRenew',
    name: 'Auto-Renew',
    lineColor: '#f472b6',
    areaColor: '#f472b6',
    areaOpacity: 1,
    strokeWidth: 0,
    renderAsBar: true,
    barWidth: 0.8,
    barPattern: 'diagonal-stripes',
    showPoints: false,
    data: []
  },
  {
    key: 'paymentFailures',
    name: 'Payment-Failures',
    lineColor: '#fbbf24',
    areaColor: 'transparent',
    areaOpacity: 0,
    strokeWidth: 2,
    lineStyle: 'solid',
    showPoints: true,
    pointRadius: 4,
    renderAsBar: false,
    data: []
  }
];

export const AI_CONTENT_TREND_CONFIG: ILineAreaSeries[] = [
  {
    key: 'totalPosts',
    name: 'Total Posts (All Platforms)',
    lineColor: '#10b981',
    areaColor: '#d1fae5',
    areaOpacity: 0.6,
    strokeWidth: 2,
    lineStyle: 'solid',
    showPoints: false,
    renderAsBar: false,
    data: []
  },
  {
    key: 'aiPosts',
    name: 'AI Posts',
    lineColor: '#c084fc',
    areaColor: '#fae8ff',
    areaOpacity: 0.5,
    strokeWidth: 2,
    lineStyle: 'solid',
    showPoints: false,
    renderAsBar: false,
    data: []
  }
];

export const AD_SPEND_TREND_CONFIG: ILineAreaSeries[] = [
  { key: 'googleSpend', name: 'Google Spend', lineColor: '#a855f7', areaColor: 'transparent', areaOpacity: 0, renderAsBar: false, showPoints: true, hollowPoints: true, pointRadius: 3, data: [] },
  { key: 'facebookSpend', name: 'Facebook Spend', lineColor: '#3b82f6', areaColor: 'transparent', areaOpacity: 0, renderAsBar: false, showPoints: true, hollowPoints: true, pointRadius: 3, data: [] },
  { key: 'instaSpend', name: 'Insta Spend', lineColor: '#f59e0b', areaColor: 'transparent', areaOpacity: 0, renderAsBar: false, showPoints: true, hollowPoints: true, pointRadius: 3, data: [] }
];

export const MARKETING_PERFORMANCE_METRICS_DEFAULT = [
  { label: 'Total Spend', value: '0', valueColorClass: 'text-gray-900 dark:text-white font-bold' },
  { label: 'Cost Per Lead', value: '0', valueColorClass: 'text-orange-400 font-semibold' },
  { label: 'Google Spend', value: '0', valueColorClass: 'text-red-500 font-semibold' },
  { label: 'Facebook Spend', value: '0', valueColorClass: 'text-gray-900 dark:text-white font-semibold' },
  { label: 'Leads / Active Ad', value: '0', valueColorClass: 'text-emerald-500 font-semibold' },
  { label: 'Deal Claim Rate', value: '0', valueColorClass: 'text-orange-400 font-semibold' },
  { label: 'Demo → Reg Rate', value: '0', valueColorClass: 'text-orange-400 font-semibold' },
  { label: 'Churn Rate', value: '0', valueColorClass: 'text-emerald-500 font-semibold' }
];

export const EXECUTIVE_PERFORMANCE_METRICS_DEFAULT = [
  { label: 'Total Signups', value: '0', valueColorClass: 'text-blue-500 font-semibold' },
  { label: 'Total AI Posts', value: '0', valueColorClass: 'text-fuchsia-500 font-semibold' },
  { label: 'Total Ad Leads', value: '0', valueColorClass: 'text-emerald-500 font-semibold' },
  { label: 'Total Ad Spend', value: '0', valueColorClass: 'text-orange-400 font-semibold' },
  { label: 'Total Deals', value: '0', valueColorClass: 'text-fuchsia-500 font-semibold' },
  { label: 'Total Demos Booked', value: '0', valueColorClass: 'text-blue-500 font-semibold' },
  { label: 'Online Revenue', value: '0', valueColorClass: 'text-red-500 font-semibold' },
  { label: 'Referral Signups', value: '0', valueColorClass: 'text-red-500 font-semibold' },
  { label: 'Freemium Feature Engagement', value: '0', valueColorClass: 'text-red-500 font-semibold' }
];
